import { Formik, Form } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useInstitutesByDistrict } from "../../../auditCalendar/hooks/useAuditCalendar";
import { useBlocksByDistrict } from "../../../block/hooks/useBlock";
import { FileUpload } from "../../../registration/helper/FileUpload";
import { useVillages } from "../../../registration/hooks/useMasterData";
import {
	fetchDistrictEmployeeByCode,
	uploadSchemeReport,
} from "../../api/SchemeDataEntry.api";
import {
	useAuditYears,
	useSchemeSubmit,
	useSchemeUpdate,
} from "../../hooks/useSchemeDataEntry";
import {
	DsjeFieldConfig,
	DsjeSectionConfig,
	getDsjeSubSchemeFormConfig,
} from "./dsjeSubSchemeFormConfig";

// A "number" field never allows a negative value — clamp to 0 instead of
// letting a stray "-2" through (raw text fields typed as numbers are left
// as-is here; only real number inputs get clamped).
const clampNonNegative = (raw: string): string => {
	if (raw === "") return raw;
	const n = Number(raw);
	if (Number.isNaN(n)) return raw;
	return n < 0 ? "0" : raw;
};

type Props = {
	subSchemeId: number;
	subSchemeName: string;
	schemeId: number | null;
	id?: string;
	initialData?: any;
	isView?: boolean;
	currentUser?: any;
	districts?: any[];
	onBackToSubSchemeList?: () => void;
};

const columnsClass = (columns: number | undefined) => {
	switch (columns) {
		case 1:
			return "row-cols-1";
		case 2:
			return "row-cols-1 row-cols-md-2";
		case 4:
			return "row-cols-1 row-cols-md-2 row-cols-xl-4";
		case 3:
		default:
			return "row-cols-1 row-cols-md-2 row-cols-xl-3";
	}
};

const buildFieldValue = (field: DsjeFieldConfig, initialData: any) => {
	const existing = initialData ? initialData[field.name] : undefined;
	return existing !== undefined && existing !== null
		? existing
		: (field.defaultValue ?? "");
};

const emptyRepeatingRow = (rowFields: DsjeFieldConfig[]) => {
	const row: Record<string, any> = {};
	rowFields.forEach((f) => {
		row[f.name] = f.defaultValue ?? "";
	});
	return row;
};

const buildInitialValues = (
	sections: DsjeSectionConfig[],
	initialData: any,
) => {
	const values: Record<string, any> = {};
	sections.forEach((section) => {
		if (section.repeating) {
			const { arrayName, rowFields } = section.repeating;
			const existingRows = initialData ? initialData[arrayName] : undefined;
			values[arrayName] =
				Array.isArray(existingRows) && existingRows.length
					? existingRows
					: [emptyRepeatingRow(rowFields)];
			return;
		}
		if (section.classTable) {
			section.classTable.rows.forEach((row) => {
				row.fields.forEach((field) => {
					values[field.name] = buildFieldValue(field, initialData);
				});
			});
			return;
		}
		if (section.maintenanceTable) {
			section.maintenanceTable.records.forEach((record) => {
				values[`${record.name}_status`] =
					(initialData && initialData[`${record.name}_status`]) ?? "";
				values[`${record.name}_currency`] =
					(initialData && initialData[`${record.name}_currency`]) ?? "";
			});
			(section.maintenanceTable.extraFields || []).forEach((field) => {
				values[field.name] = buildFieldValue(field, initialData);
			});
			return;
		}
		const fieldGroups = section.subsections
			? section.subsections.map((sub) => ({ fields: sub.fields, layout: sub.layout }))
			: [{ fields: section.fields, layout: section.layout }];

		fieldGroups.forEach(({ fields, layout }) => {
			fields.forEach((field) => {
				if (field.type === "group" && field.groupFields) {
					field.groupFields.forEach((gf) => {
						values[gf.name] = buildFieldValue(gf, initialData);
						if (gf.countField) {
							values[gf.countField.name] = buildFieldValue(gf.countField, initialData);
						}
					});
					if (field.primaryField) {
						values[field.primaryField.name] = buildFieldValue(
							field.primaryField,
							initialData,
						);
					}
					return;
				}
				values[field.name] = buildFieldValue(field, initialData);
				// A "yesno" field inside a "table" section gets an auto-added
				// Remark column, stored under `${field.name}_remark`.
				if (layout === "table" && field.type === "yesno") {
					const remarkField = { name: `${field.name}_remark` } as DsjeFieldConfig;
					values[remarkField.name] = buildFieldValue(remarkField, initialData);
				}
			});
		});
	});
	return values;
};

const YesNoToggle: React.FC<{
	value: any;
	onChange: (val: boolean) => void;
	disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
	const isYes = value === true || value === "true" || value === 1 || value === "1";
	const isNo = value === false || value === "false" || value === 0 || value === "0";

	return (
		<div className="d-flex gap-2">
			<button
				type="button"
				disabled={disabled}
				onClick={() => onChange(true)}
				className={`btn btn-sm flex-grow-1 ${isYes ? "btn-success" : "btn-light"}`}
			>
				Yes
			</button>
			<button
				type="button"
				disabled={disabled}
				onClick={() => onChange(false)}
				className={`btn btn-sm flex-grow-1 ${isNo ? "btn-danger" : "btn-light"}`}
			>
				No
			</button>
		</div>
	);
};

const GROUP_LETTERS = "abcdefghij";

const FieldRenderer: React.FC<{
	field: DsjeFieldConfig;
	value: any;
	onChange: (val: any) => void;
	disabled?: boolean;
	districts?: any[];
	institutes?: any[];
	auditYears?: any[];
	blocks?: any[];
	villages?: any[];
	// 1-based position of this field within its section — shown as
	// "N. Label", matching the numbered-question reference format.
	number?: number;
	// Only used by a "group" field: reads/writes each of its groupFields'
	// own values (keyed by their own `name`, not the group field's name).
	groupValues?: Record<string, any>;
	onGroupFieldChange?: (name: string, val: any) => void;
}> = ({
	field,
	value,
	onChange,
	disabled,
	districts,
	institutes,
	auditYears,
	blocks,
	villages,
	number,
	groupValues,
	onGroupFieldChange,
}) => {
	disabled = disabled || field.readOnly;
	const label = (
		<label className="form-label fs-7 fw-bold text-dark">
			{number !== undefined ? `${number}. ` : ""}
			{field.label}
			{field.required && <span className="text-danger ms-1">*</span>}
		</label>
	);

	if (field.type === "group" && field.groupFields) {
		const pf = field.primaryField;
		const pfValue = pf ? (groupValues?.[pf.name] ?? "") : "";
		return (
			<div className="mb-4">
				{label}
				<div className="d-flex gap-4">
					{pf && (
						<div key={pf.name} style={{ flex: "1 1 0", minWidth: 120 }}>
							{pf.type === "select" ? (
								<select
									className="form-select form-select-sm"
									value={pfValue}
									disabled={disabled || pf.readOnly}
									onChange={(e) => onGroupFieldChange?.(pf.name, e.target.value)}
								>
									<option value="">Select</option>
									{pf.options?.map((opt) => (
										<option key={opt} value={opt}>
											{opt}
										</option>
									))}
								</select>
							) : (
								<input
									type={pf.type === "number" ? "number" : "text"}
									className="form-control form-control-sm"
									value={pfValue}
									disabled={disabled || pf.readOnly}
									onChange={(e) =>
										onGroupFieldChange?.(
											pf.name,
											pf.type === "number"
												? clampNonNegative(e.target.value)
												: e.target.value,
										)
									}
								/>
							)}
						</div>
					)}
					{field.groupFields.map((gf, i) => {
						const gfValue = groupValues?.[gf.name] ?? "";
						const isComputed = !!gf.computeFrom;
						const gfDisabled = disabled || gf.readOnly || isComputed;

						if (gf.type === "checkbox") {
							const isChecked = gfValue === true || gfValue === "true";
							const cf = gf.countField;
							const cfValue = cf ? (groupValues?.[cf.name] ?? "") : "";
							return (
								<div key={gf.name} style={{ flex: "1 1 0", minWidth: 150 }}>
									{gfDisabled ? (
										// A disabled+checked native checkbox renders a
										// low-contrast (near-invisible) tick in this theme —
										// a plain badge makes the checked state unambiguous
										// on the read-only View screen.
										<span
											className={`badge d-inline-flex align-items-center gap-1 fs-8 ${
												isChecked ? "badge-light-success" : "badge-light-secondary"
											}`}
										>
											<i
												className={`bi ${isChecked ? "bi-check-circle-fill" : "bi-circle"}`}
											></i>
											{gf.label}
										</span>
									) : (
										<div className="form-check">
											<input
												type="checkbox"
												className="form-check-input"
												id={`chk-${gf.name}`}
												checked={isChecked}
												onChange={(e) => onGroupFieldChange?.(gf.name, e.target.checked)}
											/>
											<label
												className="form-check-label fs-8 fw-semibold text-dark"
												htmlFor={`chk-${gf.name}`}
											>
												{gf.label}
											</label>
										</div>
									)}
									{isChecked && cf && (
										<div className="mt-2">
											<label className="form-label fs-8 fw-semibold text-dark mb-1">
												{cf.label}
											</label>
											<input
												type="number"
												className="form-control form-control-sm"
												value={cfValue}
												disabled={gfDisabled}
												onChange={(e) =>
													onGroupFieldChange?.(cf.name, clampNonNegative(e.target.value))
												}
											/>
										</div>
									)}
								</div>
							);
						}

						const gfLabel = (
							<label className="form-label fs-8 fw-semibold text-dark mb-1">
								{GROUP_LETTERS[i] || i + 1}. {gf.label}
							</label>
						);
						return (
							<div key={gf.name} style={{ flex: "1 1 0", minWidth: 120 }}>
								{gfLabel}
								{gf.type === "select" ? (
									<select
										className="form-select form-select-sm"
										value={gfValue}
										disabled={gfDisabled}
										onChange={(e) => onGroupFieldChange?.(gf.name, e.target.value)}
									>
										<option value="">Select</option>
										{gf.options?.map((opt) => (
											<option key={opt} value={opt}>
												{opt}
											</option>
										))}
									</select>
								) : (
									<input
										type={
											gf.type === "number"
												? "number"
												: gf.type === "date"
													? "date"
													: gf.type === "time"
														? "time"
														: "text"
										}
										className={`form-control form-control-sm${isComputed ? " bg-light-primary fw-bold" : ""}`}
										value={gfValue}
										disabled={gfDisabled}
										title={isComputed ? "Auto-calculated" : undefined}
										onChange={(e) =>
											onGroupFieldChange?.(
												gf.name,
												gf.type === "number"
													? clampNonNegative(e.target.value)
													: e.target.value,
											)
										}
									/>
								)}
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	if (field.type === "district") {
		return (
			<div className="mb-4">
				{label}
				<select
					className="form-select form-select-sm"
					value={value ?? ""}
					disabled={disabled}
					onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
				>
					<option value="">Select District</option>
					{districts?.map((d: any) => (
						<option key={d.id} value={d.id}>
							{d.name}
						</option>
					))}
				</select>
			</div>
		);
	}

	if (field.type === "institute") {
		return (
			<div className="mb-4">
				{label}
				<select
					className="form-select form-select-sm"
					value={value ?? ""}
					disabled={disabled}
					onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
				>
					<option value="">Select Institute</option>
					{institutes?.map((i: any) => (
						<option key={i.id} value={i.id}>
							{i.name}
						</option>
					))}
				</select>
			</div>
		);
	}

	if (field.type === "block") {
		return (
			<div className="mb-4">
				{label}
				<select
					className="form-select form-select-sm"
					value={value ?? ""}
					disabled={disabled}
					onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
				>
					<option value="">Select Block</option>
					{blocks?.map((b: any) => (
						<option key={b.id} value={b.id}>
							{b.name}
						</option>
					))}
				</select>
			</div>
		);
	}

	if (field.type === "gram_panchayat") {
		return (
			<div className="mb-4">
				{label}
				<select
					className="form-select form-select-sm"
					value={value ?? ""}
					disabled={disabled}
					onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
				>
					<option value="">Select Gram Panchayat</option>
					{villages?.map((v: any) => (
						<option key={v.id} value={v.id}>
							{v.name}
						</option>
					))}
				</select>
			</div>
		);
	}

	if (field.type === "financial_year") {
		return (
			<div className="mb-4">
				{label}
				<select
					className="form-select form-select-sm"
					value={value ?? ""}
					disabled={disabled}
					onChange={(e) => onChange(e.target.value)}
				>
					<option value="">Select Financial Year</option>
					{auditYears?.map((y: any) => (
						<option key={y.id} value={y.name}>
							{y.name}
						</option>
					))}
				</select>
			</div>
		);
	}

	if (field.type === "yesno") {
		return (
			<div className="mb-4">
				{label}
				<YesNoToggle
					value={value}
					onChange={onChange}
					disabled={disabled}
				/>
			</div>
		);
	}

	if (field.type === "select") {
		return (
			<div className="mb-4">
				{label}
				<select
					className="form-select form-select-sm"
					value={value ?? ""}
					disabled={disabled}
					onChange={(e) => onChange(e.target.value)}
				>
					<option value="">Select</option>
					{field.options?.map((opt) => (
						<option key={opt} value={opt}>
							{opt}
						</option>
					))}
				</select>
			</div>
		);
	}

	if (field.type === "textarea") {
		return (
			<div className="mb-4">
				{label}
				<textarea
					className="form-control form-control-sm"
					rows={3}
					value={value ?? ""}
					disabled={disabled}
					placeholder={field.placeholder}
					onChange={(e) => onChange(e.target.value)}
				/>
			</div>
		);
	}

	if (field.type === "file") {
		return (
			<div className="mb-4">
				{label}
				<FileUpload
					label=""
					value={value}
					disabled={disabled}
					onChange={(val) => onChange(val as string | null)}
					onUpload={(file) =>
						uploadSchemeReport(file, field.uploadCode || "dsje-attachment")
					}
					accept=".pdf,.jpg,.jpeg,.png"
					maxSizeMB={5}
					compact
				/>
			</div>
		);
	}

	const isComputed = !!field.computeFrom;

	return (
		<div className="mb-4">
			{label}
			<input
				type={
					field.type === "number"
						? "number"
						: field.type === "date"
							? "date"
							: field.type === "time"
								? "time"
								: "text"
				}
				className={`form-control form-control-sm${isComputed ? " bg-light-primary fw-bold" : ""}`}
				value={value ?? ""}
				disabled={disabled || isComputed}
				placeholder={field.placeholder}
				title={isComputed ? "Auto-calculated" : undefined}
				onChange={(e) =>
					onChange(
						field.type === "number"
							? clampNonNegative(e.target.value)
							: e.target.value,
					)
				}
			/>
		</div>
	);
};

const AutoComputeFields: React.FC<{
	sections: DsjeSectionConfig[];
	values: Record<string, any>;
	setFieldValue: (field: string, value: any) => void;
}> = ({ sections, values, setFieldValue }) => {
	const computedFields = sections
		.flatMap((section) => [
			...section.fields,
			...section.fields.flatMap((f) => f.groupFields ?? []),
			...(section.subsections?.flatMap((sub) => sub.fields) ?? []),
			...(section.classTable?.rows.flatMap((row) => row.fields) ?? []),
			...(section.repeating?.rowFields ?? []),
		])
		.filter((field) => field.computeFrom && field.computeFrom.length > 0);

	const dependencyKey = computedFields
		.flatMap((field) => field.computeFrom!.map((name) => values[name]))
		.join("|");

	useEffect(() => {
		computedFields.forEach((field) => {
			const validValues = field
				.computeFrom!.map((name) => values[name])
				.filter((v) => v !== "" && v !== null && v !== undefined && !isNaN(Number(v)))
				.map(Number);
			const avg = validValues.length
				? Math.round((validValues.reduce((a, b) => a + b, 0) / validValues.length) * 100) / 100
				: "";
			if (values[field.name] !== avg) {
				setFieldValue(field.name, avg);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dependencyKey]);

	return null;
};

const DsjeSubSchemeForm: React.FC<Props> = ({
	subSchemeId,
	subSchemeName,
	schemeId,
	id,
	initialData,
	isView = false,
	currentUser,
	districts,
	onBackToSubSchemeList,
}) => {
	const navigate = useNavigate();
	const submitMutation = useSchemeSubmit();
	const updateMutation = useSchemeUpdate();
	const [brpSelectedDistrict, setBrpSelectedDistrict] = useState<
		number | null
	>(Number(initialData?.brp_district_id) || Number(currentUser?.district_id) || null);
	const { data: brpBlocksData } = useBlocksByDistrict(brpSelectedDistrict || 0);
	const brpBlocks = brpBlocksData || [];
	// Called unconditionally (before the `!config` early return below) since
	// hooks can't be called conditionally — cheap/cached and harmless for
	// every other sub-scheme's form too, which simply never renders a
	// "financial_year" field so this data goes unused.
	const { data: auditYears } = useAuditYears();
	// Only meaningful for a `tabs` config (see below) — a tabbed form saves
	// progress per tab, so it needs to track which entry id it's updating
	// after the very first tab creates one, exactly like NMP/CMBFS's
	// stepper. Harmless to declare unconditionally for a non-tabbed config.
	const [currentTab, setCurrentTab] = useState(0);
	const [currentId, setCurrentId] = useState<string | undefined>(id);

	const config = getDsjeSubSchemeFormConfig(subSchemeName);

	// `allSections` and the `initialValues` memo below are computed
	// unconditionally (before the `!config` early return further down) so
	// every hook in this component runs on every render regardless of
	// whether `config` resolves yet — `subSchemeName` can arrive a render
	// late (e.g. View/Edit waiting on the sub-scheme list to load), and a
	// hook declared after a conditional return would then be skipped on the
	// first render but present on a later one, which React treats as a
	// hooks-order violation and crashes the whole page.
	const allSections = config
		? config.tabs
			? config.tabs.flatMap((tab) => tab.sections)
			: config.sections
		: [];

	// Recomputed only when the underlying saved data actually changes content
	// (keyed by a JSON snapshot, not object reference — the parent page
	// rebuilds `initialData` as a fresh object on every render even when
	// nothing changed). This must stay referentially stable across a tabbed
	// form's own tab transitions: `enableReinitialize` compares this object
	// against Formik's internal baseline on every render, and each
	// "Submit & Next" advances that baseline via `resetForm({ values })` —
	// if `initialValues` were a new (if content-equal-at-mount) object every
	// render, the very next render after a tab's resetForm would look like a
	// real prop change and Formik would reinitialize back to this (stale,
	// blank) snapshot, silently wiping every already-saved tab's data.
	const initialDataKey = JSON.stringify(initialData ?? null);
	const initialValues = useMemo(
		() => ({
			...buildInitialValues(allSections, initialData),
			brp_employee_code:
				initialData?.brp_employee_code || currentUser?.employee_code || "",
			brp_name: initialData?.brp_name || currentUser?.name || "",
			brp_mobile_no: initialData?.brp_mobile_no || currentUser?.mobile || "",
			brp_district_id:
				initialData?.brp_district_id || currentUser?.district_id || "",
			brp_block_id: initialData?.brp_block_id || currentUser?.block_id || "",
		}),
		// `subSchemeName` is included because `allSections` depends on it via
		// `config`: on View/Edit, this component can render once with the
		// sub-scheme list still loading (`subSchemeName` momentarily "",
		// `allSections` momentarily []) before the real name arrives a render
		// later — without this dependency the memo would stay cached from
		// that empty-sections pass and never pick up the real fields once
		// `config` resolves, even though `initialData` itself never changed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			initialDataKey,
			subSchemeName,
			currentUser?.employee_code,
			currentUser?.name,
			currentUser?.mobile,
			currentUser?.district_id,
			currentUser?.block_id,
		],
	);

	if (!config) {
		return (
			<div className="text-center text-muted py-10">
				No form is configured yet for "{subSchemeName}".
			</div>
		);
	}

	const activeSections = config.tabs
		? config.tabs[currentTab].sections
		: config.sections;
	const lastTabIndex = (config.tabs?.length ?? 1) - 1;

	// At most one "institute"/"block"/"gram_panchayat" field is expected per
	// config — each one's `dependsOn` (or its own default) names the sibling
	// field holding the id to scope its list by.
	const instituteField = allSections
		.flatMap((section) => section.fields)
		.find((field) => field.type === "institute");
	const blockField = allSections
		.flatMap((section) => section.fields)
		.find((field) => field.type === "block");
	const gpField = allSections
		.flatMap((section) => section.fields)
		.find((field) => field.type === "gram_panchayat");

	const handleSubmit = async (values: Record<string, any>, actions: any) => {
		try {
			const dataToSave = {
				...values,
				scheme_id: schemeId,
				sub_scheme_id: subSchemeId,
			};

			if (!config.tabs) {
				if (id) {
					await updateMutation.mutateAsync({ id, data: dataToSave });
					toast.success("Entry updated successfully");
					navigate("/scheme-data-entry");
				} else {
					await submitMutation.mutateAsync(dataToSave);
					toast.success("Entry submitted successfully");
					if (onBackToSubSchemeList) {
						onBackToSubSchemeList();
					} else {
						navigate("/scheme-data-entry");
					}
				}
				return;
			}

			// Tabbed form: each "Submit & Next" saves progress immediately —
			// create on the first tab, update on every tab after that.
			let newId = currentId;
			if (currentId) {
				await updateMutation.mutateAsync({ id: currentId, data: dataToSave });
			} else {
				const res = await submitMutation.mutateAsync(dataToSave);
				if (res?.data?.id) {
					newId = res.data.id;
					setCurrentId(newId);
				}
			}

			if (currentTab === lastTabIndex) {
				// Edit mode must navigate away outright: `onBackToSubSchemeList`
				// only clears the parent's `selectedSubSchemeId`, but editing an
				// existing entry keeps `initialData` populated, so the parent's
				// own effect immediately re-derives the same sub-scheme id from
				// it and remounts this form back onto tab 1 — a confusing
				// bounce instead of a clean exit.
				if (id) {
					toast.success("Entry updated successfully");
					navigate("/scheme-data-entry");
				} else {
					toast.success("Entry submitted successfully");
					if (onBackToSubSchemeList) {
						onBackToSubSchemeList();
					} else {
						navigate("/scheme-data-entry");
					}
				}
			} else {
				toast.success(`${config.tabs[currentTab].title} saved successfully`);
				actions.resetForm({ values });
				setCurrentTab(currentTab + 1);
			}
		} catch (error: any) {
			console.error("DSJE submission error:", error);
			const errorMessage =
				error.response?.data?.message ||
				error.response?.data?.error ||
				error.message ||
				"Failed to submit entry";
			toast.error(errorMessage);
		} finally {
			actions.setSubmitting?.(false);
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			enableReinitialize
			onSubmit={handleSubmit}
		>
			{({ values, setFieldValue, isSubmitting }) => {
				const { institutes } = useInstitutesByDistrict(
					instituteField
						? values[instituteField.dependsOn || "district_id"] || null
						: null,
				);
				const { data: tabBlocksData } = useBlocksByDistrict(
					blockField ? values[blockField.dependsOn || "district_id"] || undefined : undefined,
				);
				const tabBlocks = tabBlocksData || [];
				const { data: gpVillagesData } = useVillages(
					gpField ? values[gpField.dependsOn || "block_id"] || undefined : undefined,
				);
				const gpVillages = gpVillagesData || [];

				const renderOneField = (field: DsjeFieldConfig, index: number, keyPrefix: string) => (
					<FieldRenderer
						key={`${keyPrefix}-${field.name}`}
						field={field}
						value={values[field.name]}
						disabled={isView}
						districts={districts}
						institutes={institutes}
						auditYears={auditYears}
						blocks={tabBlocks}
						villages={gpVillages}
						number={index + 1}
						groupValues={values}
						onGroupFieldChange={(name, val) => setFieldValue(name, val)}
						onChange={(val) => setFieldValue(field.name, val)}
					/>
				);

				// A "group" field needs the whole row's width to lay its own
				// sub-fields out horizontally, so it can't share a Bootstrap
				// row-cols-* grid with regular fields (whose per-item width
				// that grid enforces via a `> *` selector, which beats a
				// plain `col-12` override). Consecutive non-group fields
				// still share the normal multi-column grid; each group gets
				// its own unconstrained full-width block — in original
				// field order either way, so numbering stays sequential.
				const renderFieldsGrid = (
					fields: DsjeFieldConfig[],
					columns: 1 | 2 | 3 | 4 | undefined,
					keyPrefix: string,
				) => {
					const chunks: { isGroup: boolean; items: { field: DsjeFieldConfig; index: number }[] }[] = [];
					fields.forEach((field, index) => {
						const isGroup = field.type === "group";
						const last = chunks[chunks.length - 1];
						if (last && last.isGroup === isGroup) {
							last.items.push({ field, index });
						} else {
							chunks.push({ isGroup, items: [{ field, index }] });
						}
					});

					return (
						<div>
							{chunks.map((chunk, ci) =>
								chunk.isGroup ? (
									<div key={`${keyPrefix}-chunk-${ci}`}>
										{chunk.items.map(({ field, index }) => (
											<div className="mb-4" key={`${keyPrefix}-${field.name}`}>
												{renderOneField(field, index, keyPrefix)}
											</div>
										))}
									</div>
								) : (
									<div
										className={`row ${columnsClass(columns)} g-4 mb-4`}
										key={`${keyPrefix}-chunk-${ci}`}
									>
										{chunk.items.map(({ field, index }) => (
											<div className="col" key={`${keyPrefix}-${field.name}`}>
												{renderOneField(field, index, keyPrefix)}
											</div>
										))}
									</div>
								),
							)}
						</div>
					);
				};

				const renderFieldsTable = (fields: DsjeFieldConfig[], keyPrefix: string) => (
					<div className="table-responsive border rounded">
						<table className="table table-bordered table-sm mb-0 align-middle">
							<thead>
								<tr>
									<th style={{ background: "#1e2b5c", color: "#fff" }}>
										Question / Parameter
									</th>
									<th
										style={{ width: 70, background: "#1e2b5c", color: "#fff" }}
										className="text-center"
									>
										Yes
									</th>
									<th
										style={{ width: 70, background: "#1e2b5c", color: "#fff" }}
										className="text-center"
									>
										No
									</th>
									<th style={{ width: 240, background: "#1e2b5c", color: "#fff" }}>
										Remark
									</th>
								</tr>
							</thead>
							<tbody>
								{fields.map((field, index) => {
									const isYesNo = field.type === "yesno";
									const remarkName = `${field.name}_remark`;
									return (
										<tr key={`${keyPrefix}-${field.name}`}>
											<td className="fw-semibold">
												{index + 1}. {field.label}
											</td>
											{isYesNo ? (
												<>
													<td className="text-center">
														<input
															type="radio"
															className="form-check-input"
															checked={values[field.name] === "Yes"}
															disabled={isView}
															onChange={() => setFieldValue(field.name, "Yes")}
														/>
													</td>
													<td className="text-center">
														<input
															type="radio"
															className="form-check-input"
															checked={values[field.name] === "No"}
															disabled={isView}
															onChange={() => setFieldValue(field.name, "No")}
														/>
													</td>
													<td>
														<input
															className="form-control form-control-sm"
															value={values[remarkName] ?? ""}
															disabled={isView}
															placeholder="Enter Remark"
															onChange={(e) =>
																setFieldValue(remarkName, e.target.value)
															}
														/>
													</td>
												</>
											) : (
												<td colSpan={3}>
													<input
														type={field.type === "number" ? "number" : "text"}
														className="form-control form-control-sm"
														value={values[field.name] ?? ""}
														disabled={isView}
														onChange={(e) =>
															setFieldValue(
																field.name,
																field.type === "number"
																	? clampNonNegative(e.target.value)
																	: e.target.value,
															)
														}
													/>
												</td>
											)}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				);

				const renderPairedRows = (fields: DsjeFieldConfig[], keyPrefix: string) => {
					const rows: [DsjeFieldConfig, DsjeFieldConfig | undefined][] = [];
					for (let i = 0; i < fields.length; i += 2) {
						rows.push([fields[i], fields[i + 1]]);
					}
					return (
						<div className="border rounded overflow-hidden">
							<div className="row g-0 px-4 py-2 bg-light-subtle border-bottom">
								<div className="col-md-5 fw-bold text-dark fs-8 text-uppercase">
									Documents and Registers
								</div>
								<div className="col-md-7 fw-bold text-dark fs-8 text-uppercase">
									Availability
								</div>
							</div>
							{rows.map(([question, remark], rowIndex) => (
								<div
									key={`${keyPrefix}-${question.name}`}
									className={`row g-3 align-items-start p-4 ${rowIndex % 2 === 1 ? "bg-light-subtle" : ""}`}
								>
									<div className="col-md-5 fw-semibold text-dark pt-2">
										{question.label}
									</div>
									<div className="col-md-3">
										<FieldRenderer
											field={{ ...question, label: "" }}
											value={values[question.name]}
											disabled={isView}
											districts={districts}
											institutes={institutes}
											auditYears={auditYears}
											onChange={(val) => setFieldValue(question.name, val)}
										/>
									</div>
									{remark && (
										<div className="col-md-4">
											<FieldRenderer
												field={{ ...remark, label: "", type: "textarea" }}
												value={values[remark.name]}
												disabled={isView}
												onChange={(val) => setFieldValue(remark.name, val)}
											/>
										</div>
									)}
								</div>
							))}
						</div>
					);
				};

				const renderClassTable = (classTable: NonNullable<DsjeSectionConfig["classTable"]>) => (
					<div className="table-responsive border rounded">
						<table className="table table-bordered mb-0 align-middle">
							<thead>
								<tr className="bg-light-subtle">
									<th style={{ width: 150 }}>{classTable.rowLabelHeader}</th>
									<th>{classTable.valueHeader}</th>
								</tr>
							</thead>
							<tbody>
								{classTable.rows.map((row) => (
									<tr key={row.label}>
										<td className="fw-bold">{row.label}:</td>
										<td>
											<div className="d-flex gap-4 flex-wrap">
												{row.fields.map((f) => (
													<div key={f.name} style={{ minWidth: 150 }}>
														{row.fields.length > 1 && (
															<label className="form-label fs-8 fw-semibold text-dark mb-1">
																{f.label}:
															</label>
														)}
														<input
															type={f.type === "number" ? "number" : "text"}
															className="form-control form-control-sm"
															value={values[f.name] ?? ""}
															disabled={isView}
															onChange={(e) =>
																setFieldValue(
																	f.name,
																	f.type === "number"
																		? clampNonNegative(e.target.value)
																		: e.target.value,
																)
															}
														/>
													</div>
												))}
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				);

				const renderMaintenanceTable = (
					maintenanceTable: NonNullable<DsjeSectionConfig["maintenanceTable"]>,
				) => (
					<div>
						<div className="table-responsive border rounded mb-4">
							<table className="table table-bordered table-sm mb-0 align-middle">
								<thead>
									<tr>
										<th style={{ width: 60, background: "#1e2b5c", color: "#fff" }}>
											Sr. No
										</th>
										<th style={{ background: "#1e2b5c", color: "#fff" }}>Records</th>
										<th style={{ width: 60, background: "#1e2b5c", color: "#fff" }} className="text-center">
											Yes
										</th>
										<th style={{ width: 60, background: "#1e2b5c", color: "#fff" }} className="text-center">
											No
										</th>
										<th style={{ width: 90, background: "#1e2b5c", color: "#fff" }} className="text-center">
											Updated
										</th>
										<th style={{ width: 110, background: "#1e2b5c", color: "#fff" }} className="text-center">
											Not Updated
										</th>
									</tr>
								</thead>
								<tbody>
									{maintenanceTable.records.map((record, index) => {
										const statusName = `${record.name}_status`;
										const currencyName = `${record.name}_currency`;
										return (
											<tr key={record.name}>
												<td>{index + 1}</td>
												<td>{record.label}</td>
												<td className="text-center">
													<input
														type="radio"
														className="form-check-input"
														checked={values[statusName] === "Yes"}
														disabled={isView}
														onChange={() => setFieldValue(statusName, "Yes")}
													/>
												</td>
												<td className="text-center">
													<input
														type="radio"
														className="form-check-input"
														checked={values[statusName] === "No"}
														disabled={isView}
														onChange={() => setFieldValue(statusName, "No")}
													/>
												</td>
												<td className="text-center">
													<input
														type="radio"
														className="form-check-input"
														checked={values[currencyName] === "Updated"}
														disabled={isView}
														onChange={() => setFieldValue(currencyName, "Updated")}
													/>
												</td>
												<td className="text-center">
													<input
														type="radio"
														className="form-check-input"
														checked={values[currencyName] === "Not Updated"}
														disabled={isView}
														onChange={() => setFieldValue(currencyName, "Not Updated")}
													/>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
						{maintenanceTable.extraFields &&
							renderFieldsGrid(maintenanceTable.extraFields, 3, "maintenance-extra")}
					</div>
				);

				return (
				<Form>
					<AutoComputeFields
						sections={allSections}
						values={values}
						setFieldValue={setFieldValue}
					/>
					<div
						className="mb-6"
						style={{
							height: 3,
							borderRadius: 2,
							background: `linear-gradient(90deg, ${config.colorHex}, ${config.colorHex}aa)`,
						}}
					/>

					{(!config.tabs || currentTab === 0) && (
					<div className="mb-6">
						<div className="text-primary fw-bold fs-7 text-uppercase mb-3">
							BRP details (auto-filled)
						</div>
						<div className={`row ${columnsClass(4)} g-4`}>
							<div className="col mb-4">
								<label className="form-label fs-7 fw-bold text-dark">
									Employee code
								</label>
								<input
									className="form-control form-control-sm fw-bold text-dark"
									value={values.brp_employee_code || ""}
									disabled={true}
									onChange={async (e) => {
										const val = e.target.value;
										setFieldValue("brp_employee_code", val);
										if (val && val.length >= 4) {
											try {
												const details =
													await fetchDistrictEmployeeByCode(val);
												const employee = Array.isArray(details)
													? details[0]
													: details;
												if (employee) {
													setFieldValue("brp_name", employee.name || "");
													setFieldValue(
														"brp_mobile_no",
														employee.mobile_no || employee.mobile || "",
													);
													if (employee.district_id) {
														setFieldValue(
															"brp_district_id",
															employee.district_id,
														);
														setBrpSelectedDistrict(employee.district_id);
													}
													if (employee.block_id) {
														setFieldValue(
															"brp_block_id",
															employee.block_id,
														);
													}
												}
											} catch (error) {
												console.error(
													"Failed to fetch BRP details",
													error,
												);
											}
										}
									}}
								/>
							</div>
							<div className="col mb-4">
								<label className="form-label fs-7 fw-bold text-dark">
									Name
								</label>
								<input
									className="form-control form-control-sm fw-bold text-dark"
									value={values.brp_name || ""}
									disabled={true}
									onChange={(e) => setFieldValue("brp_name", e.target.value)}
								/>
							</div>
							<div className="col mb-4">
								<label className="form-label fs-7 fw-bold text-dark">
									Mobile no
								</label>
								<input
									className="form-control form-control-sm fw-bold text-dark"
									value={values.brp_mobile_no || ""}
									disabled={true}
									onChange={(e) =>
										setFieldValue("brp_mobile_no", e.target.value)
									}
								/>
							</div>
							<div className="col mb-4">
								<label className="form-label fs-7 fw-bold text-dark">
									District
								</label>
								<select
									className="form-select form-select-sm fw-bold text-dark"
									value={values.brp_district_id || ""}
									disabled={true}
									onChange={(e) => {
										const val = Number(e.target.value) || "";
										setFieldValue("brp_district_id", val);
										setFieldValue("brp_block_id", "");
										setBrpSelectedDistrict(val || null);
									}}
								>
									<option value="">Select District</option>
									{districts?.map((d: any) => (
										<option key={d.id} value={d.id}>
											{d.name}
										</option>
									))}
								</select>
							</div>
							<div className="col mb-4">
								<label className="form-label fs-7 fw-bold text-dark">
									Block
								</label>
								<select
									className="form-select form-select-sm fw-bold text-dark"
									value={values.brp_block_id || ""}
									disabled={true}
									onChange={(e) =>
										setFieldValue(
											"brp_block_id",
											Number(e.target.value) || "",
										)
									}
								>
									<option value="">Select Block</option>
									{brpBlocks?.map((b: any) => (
										<option key={b.id} value={b.id}>
											{b.name}
										</option>
									))}
								</select>
							</div>
						</div>
					</div>
					)}

					{config.tabs && (
						<div className="mb-6">
							<div className="d-flex flex-wrap gap-6 border-bottom">
								{config.tabs.map((tab, i) => (
									<button
										key={tab.title}
										type="button"
										className={`btn btn-link text-decoration-none text-nowrap px-0 py-3 rounded-0 fs-6 ${
											i === currentTab
												? "fw-bold text-dark border-bottom border-3 border-primary"
												: "text-muted"
										}`}
										onClick={() => setCurrentTab(i)}
									>
										{tab.title}
									</button>
								))}
							</div>
							<h3 className="fw-bold text-dark mt-5 mb-0">
								{config.tabs[currentTab].title}
							</h3>
						</div>
					)}

					{activeSections.map((section, sectionIndex) => {
						const sectionHeader = (
							<div className="d-flex align-items-center gap-3 mb-4">
								<div
									style={{ height: 2, width: 32, background: config.colorHex }}
								/>
								<div className="fw-bold fs-4 text-dark">{section.title}</div>
								<div
									style={{ height: 2, width: 32, background: config.colorHex }}
								/>
							</div>
						);

						if (section.repeating) {
							const { arrayName, rowFields } = section.repeating;
							const rows: Record<string, any>[] = values[arrayName] || [];
							return (
								<div className="mb-6" key={`${section.title}-${sectionIndex}`}>
									{sectionHeader}
									{!isView && (
										<button
											type="button"
											className="btn btn-sm btn-success mb-4"
											onClick={() =>
												setFieldValue(arrayName, [
													...rows,
													emptyRepeatingRow(rowFields),
												])
											}
										>
											Add
										</button>
									)}
									{rows.map((row, rowIndex) => (
										<div key={rowIndex} className="border rounded p-4 mb-4 bg-light-lighten">
											<div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4">
												{rowFields.map((rf, fieldIndex) => (
													<div className="col" key={rf.name}>
														<FieldRenderer
															field={rf}
															value={row[rf.name]}
															disabled={isView}
															number={fieldIndex + 1}
															onChange={(val) => {
																const newRows = rows.map((r, i) =>
																	i === rowIndex ? { ...r, [rf.name]: val } : r,
																);
																setFieldValue(arrayName, newRows);
															}}
														/>
													</div>
												))}
											</div>
											{!isView && rows.length > 1 && (
												<button
													type="button"
													className="btn btn-sm btn-danger mt-4"
													onClick={() =>
														setFieldValue(
															arrayName,
															rows.filter((_, i) => i !== rowIndex),
														)
													}
												>
													Remove
												</button>
											)}
										</div>
									))}
								</div>
							);
						}

						if (section.classTable) {
							return (
								<div className="mb-6" key={`${section.title}-${sectionIndex}`}>
									{sectionHeader}
									{renderClassTable(section.classTable)}
								</div>
							);
						}

						if (section.maintenanceTable) {
							return (
								<div className="mb-6" key={`${section.title}-${sectionIndex}`}>
									{sectionHeader}
									{renderMaintenanceTable(section.maintenanceTable)}
								</div>
							);
						}

						if (section.subsections) {
							return (
								<div className="mb-6" key={`${section.title}-${sectionIndex}`}>
									{sectionHeader}
									<div className="card border shadow-sm p-4">
										{section.subsections.map((sub, subIndex) => (
											<div
												className={subIndex > 0 ? "mt-6" : ""}
												key={`${section.title}-${sectionIndex}-${subIndex}`}
											>
												<div className="fw-bold fs-6 text-primary mb-3">
													{sub.title}
												</div>
												{sub.layout === "table"
													? renderFieldsTable(
															sub.fields,
															`${sectionIndex}-${subIndex}`,
														)
													: renderFieldsGrid(
															sub.fields,
															sub.columns,
															`${sectionIndex}-${subIndex}`,
														)}
											</div>
										))}
									</div>
								</div>
							);
						}

						if (section.layout === "table") {
							return (
								<div className="mb-6" key={`${section.title}-${sectionIndex}`}>
									{sectionHeader}
									{renderFieldsTable(section.fields, String(sectionIndex))}
								</div>
							);
						}

						if (section.layout === "paired") {
							return (
								<div className="mb-6" key={`${section.title}-${sectionIndex}`}>
									{sectionHeader}
									{renderPairedRows(section.fields, String(sectionIndex))}
								</div>
							);
						}

						return (
							<div className="mb-6" key={`${section.title}-${sectionIndex}`}>
								{sectionHeader}
								{renderFieldsGrid(section.fields, section.columns, String(sectionIndex))}
							</div>
						);
					})}

					{!isView && (
						<div className="d-flex justify-content-end gap-3 pt-4 border-top">
							{config.tabs && currentTab > 0 && (
								<button
									type="button"
									className="btn btn-light-primary me-auto"
									onClick={() => setCurrentTab(currentTab - 1)}
								>
									Previous
								</button>
							)}
							<button
								type="button"
								className="btn btn-light"
								onClick={() => navigate("/scheme-data-entry")}
							>
								Cancel
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={isSubmitting}
							>
								{isSubmitting
									? "Submitting..."
									: config.tabs
										? currentTab === lastTabIndex
											? "Submit"
											: "Submit & Next"
										: id
											? "Update entry"
											: "Save entry"}
							</button>
						</div>
					)}

					{isView && config.tabs && (
						<div className="d-flex justify-content-between gap-3 pt-4 border-top">
							{currentTab > 0 ? (
								<button
									type="button"
									className="btn btn-light-primary"
									onClick={() => setCurrentTab(currentTab - 1)}
								>
									Previous
								</button>
							) : (
								<span />
							)}
							{currentTab < lastTabIndex && (
								<button
									type="button"
									className="btn btn-light-primary"
									onClick={() => setCurrentTab(currentTab + 1)}
								>
									Next
								</button>
							)}
						</div>
					)}
				</Form>
				);
			}}
		</Formik>
	);
};

export default DsjeSubSchemeForm;
